import { useState } from "react";
import { useLogin, } from "../Use-auth";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/Card";
import ErrorToast from "../ui/ErrorToast";
import { Link } from "wouter";
import { api } from "../../lib/api";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { mutate: login, isPending } = useLogin();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForceLogin, setShowForceLogin] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [isProcessingForceLogin, setIsProcessingForceLogin] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!username || !password) return;

    login({ username, password }, {
      onError: (err) => {
        if (err.response?.activeSessionExists) {
          setPendingLoginData({ username, password });
          setShowForceLogin(true);
        } else {
          setError(err.message);
        }
      },
      onSuccess: (user) => {
        console.log("LOGIN SUCCESS:", user);

        setError(null);

        const authData = {
          userId: user.userId,
          username: user.username,
          loginTime: new Date().toISOString(),
        };

        localStorage.setItem("auth_user", JSON.stringify(authData));
      },

    });
  };

  const handleForceLogin = async () => {
    if (!pendingLoginData) return;

    const { username: savedUsername, password: savedPassword } = pendingLoginData;

    try {
      await fetch(api.auth.forceLogout.path, {
        method: api.auth.forceLogout.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: savedUsername }),
        credentials: "include",
      });

      setShowForceLogin(false);

      setIsProcessingForceLogin(true);
      setUsername(savedUsername);
      setPassword(savedPassword);

      setPendingLoginData(null);
      setError(null);

      setTimeout(() => {
        login({ username: savedUsername, password: savedPassword, force: true }, {
          onSuccess: (user) => {
            localStorage.setItem("auth_user", JSON.stringify({
              userId: user.userId,
              username: user.username,
              department: user.department,
            }));
            localStorage.setItem("session_start", user.sessionStart);
            setError(null);
            setIsProcessingForceLogin(false);
          },
          onError: (err) => {
            setError(err.message);
            setIsProcessingForceLogin(false);
          },
        });
      }, 3000);

    } catch (err) {
      console.error("Force logout failed:", err);
      setError("Failed to terminate old session. Try again.");
      setIsProcessingForceLogin(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background relative overflow-hidden"
      style={{ backgroundImage: 'linear-gradient(to top, #48c6ef 0%, #6f86d6 100%)' }}>

      <Card className="w-full max-w-md border border-border/50 shadow-2xl bg-white/80 backdrop-blur-xl relative z-10 rounded-2xl">
        <CardHeader className="space-y-3 text-center pb-4">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-2" >
            <img src="https://www.hubsyntax.com/Hubsyntax-logo.png" alt="HubSyntax Logo" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">Welcome back</CardTitle>
          <CardDescription>Enter your credentials to access the workspace</CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground ml-1">Username</label>
              <Input
                type="text"
                placeholder="Enter your username"
                className="h-12 rounded-xl bg-secondary/50 border-transparent focus:bg-background focus:border-primary transition-all"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="space-y-2 relative">
              <label className="text-sm font-medium text-foreground ml-1">Password</label>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className="h-12 rounded-xl bg-secondary/50 border-transparent focus:bg-background focus:border-primary pr-12 transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div
                className="absolute right-3 top-[43px] transform -translate-y-1/2 cursor-pointer text-gray-500"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} color="grey" /> : <Eye size={16} color="grey" />}
              </div>
            </div>

            <div className="text-right">
              <Link href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                Forgot Password?
              </Link>
            </div>

            <Button
              type="submit"
              variant="success"
              className="w-full h-9 sm:h-12 rounded-xl text-base font-bold shadow-lg shadow-primary/25 mt-6 hover:translate-y-[-2px] transition-all bg-green-500 text-white"
              disabled={isPending || isProcessingForceLogin}
            >
              {(isPending || isProcessingForceLogin)
                ? <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                : <>Sign In <ArrowRight className="ml-2 h-5 w-5" /></>}
            </Button>

            <div className="text-center pt-2">
              <p className="text-sm text-muted-foreground">
                Don't have an account? Contact your administrator.
              </p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Error Toast */}
      <ErrorToast
        message={error}
        duration={4000}
        onClose={() => setError(null)}
      />

      {/* Force Login Popup */}
      {showForceLogin && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4">
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg space-y-4 text-center w-full max-w-md">

            <p className="text-sm sm:text-base">
              Your account is logged in elsewhere.
            </p>

            <p className="text-sm sm:text-base">
              Do you want to log out from other session and continue?
            </p>

            <div className="flex flex-col zy:flex-row gap-3 sm:gap-3 sm:justify-center mt-4">

              <button
                className="w-full sm:w-auto px-4 py-2 text-[12px] sm:text-[15px] rounded bg-gray-200 hover:bg-gray-300"
                onClick={() => setShowForceLogin(false)}
              >
                Cancel
              </button>

              <button
                className="w-full sm:w-auto px-4 py-2 text-[12px] sm:text-[15px] rounded bg-red-500 text-white hover:bg-red-600"
                onClick={handleForceLogin}
                disabled={isPending}
              >
                {isPending ? "Processing..." : "Force Login"}
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
